import React, { FC } from 'react';

import { useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import IKImage from '~/components/IKImage/IKImage';
import { PlayButton } from '~/components/Play/Play';
import { SMALL_MARGIN_PX } from '~/theme';
import { Maybe } from '~/types';
import CompletedTick from '~/ui/components/CompletedTick/CompletedTick';

interface VideoPreviewProps {
  completed?: boolean;
  withPlayIcon?: boolean;
  loading?: boolean;
  imageUrl?: Maybe<string>;
  alt?: Maybe<string>;
  size?: 'sm' | 'md';
}

const sizes = {
  sm: {
    width: 62,
    height: 44,
  },
  md: {
    width: 88,
    height: 64,
  },
};

const VideoPreview: FC<VideoPreviewProps> = ({
  completed,
  alt,
  imageUrl,
  loading,
  withPlayIcon,
  size = 'sm',
  ...rest
}) => {
  const theme = useTheme();

  const dimensions = sizes[size];

  if (loading) {
    return <LoadingPreviewWrapper height={dimensions.height} width={dimensions.width} />;
  }

  if (!imageUrl) {
    return null;
  }

  const VideoIcon = completed ? (
    <CompletedIcon size="xs" data-testid={rest['data-testid'] + '/CompletedIcon'} />
  ) : withPlayIcon ? (
    <PlayButton
      data-testid={rest['data-testid'] + '/PlayIcon'}
      color={theme.palette.secondary.contrastText}
      xs={22}
    />
  ) : null;

  return (
    <PreviewWrapper completed={completed} height={dimensions.height} width={dimensions.width}>
      <Image
        path={imageUrl}
        alt={alt}
        srcset={[{ width: dimensions.width, height: dimensions.height, crop: 'maintain_ratio' }]}
        sizes={`${dimensions.width}px`}
        draggable={false}
        data-object-fit="cover"
        data-testid={rest['data-testid'] + '/Image'}
      />

      {VideoIcon}
    </PreviewWrapper>
  );
};

export default VideoPreview;

export const createPreviewWrapper = (tag) => styled(tag, {
  shouldForwardProp: (prop) => prop !== 'completed' && prop !== 'height' && prop !== 'width',
})<{
  completed: boolean;
  height: number;
  width: number;
}>`
  position: relative;
  margin-right: ${SMALL_MARGIN_PX};
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
  min-width: ${({ width }) => width}px;

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.palette.common.card.overlay.video};
    opacity: ${({ completed }) => (completed ? 0.5 : 0)};
  }
`;
export const PreviewWrapper = createPreviewWrapper('div');
export const LoadingPreviewWrapper = createPreviewWrapper(AnimatedSkeleton);
export const CompletedIcon = styled(CompletedTick)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;
export const Image = styled(IKImage)`
  width: 100%;
  height: 100%;
`;
